import { z } from "zod";
import { CalendarDateSchema } from "../campaign-creation";
import { SmartCreationRecommendationApplicabilitySchema } from "./objectives";
import {
  createDefaultSmartCreationTargetingStrategyConfiguration,
  SmartCreationStrategySeedRuleValueShape,
  SmartCreationTargetingStrategyBranchSchema,
  type SmartCreationTargetingStrategyConfiguration,
  SmartCreationTargetingStrategyConfigurationSchema,
  validateSmartCreationStrategySeedRule,
} from "./strategy-configuration";
import {
  SmartCreationAutoTargetExpressionSchema,
  SmartCreationStrategyEntityIdSchema,
  SmartCreationStrategyLaneSchema,
  SmartCreationStrategyTargetSourceSchema,
  SmartCreationStrategyTargetValueSchema,
  type SmartCreationTargetingStrategyKind,
  SmartCreationTargetingStrategyKindSchema,
} from "./strategy-targets";

export const SmartCreationStrategyCapabilityReasonSchema = z.enum([
  "not_connected",
  "not_implemented",
  "verification_required",
  "insufficient_evidence",
  "unsupported_for_sp_mvp",
]);
export type SmartCreationStrategyCapabilityReason = z.infer<
  typeof SmartCreationStrategyCapabilityReasonSchema
>;

export const SmartCreationStrategyCapabilitySchema = z.discriminatedUnion("supported", [
  z.object({ supported: z.literal(true), reason: z.null() }).strict(),
  z
    .object({
      supported: z.literal(false),
      reason: SmartCreationStrategyCapabilityReasonSchema,
    })
    .strict(),
]);
export type SmartCreationStrategyCapability = z.infer<typeof SmartCreationStrategyCapabilitySchema>;

export const SmartCreationStrategyCapabilitiesSchema = z
  .object({
    lanes: z
      .record(SmartCreationStrategyLaneSchema, SmartCreationStrategyCapabilitySchema)
      .optional(),
    sources: z
      .object({
        currentShopSearchTermPerformance: SmartCreationStrategyCapabilitySchema,
        currentShopTargetPerformance: SmartCreationStrategyCapabilitySchema,
        amazonRecommendations: SmartCreationStrategyCapabilitySchema,
        userInput: SmartCreationStrategyCapabilitySchema,
        keywordLibrary: SmartCreationStrategyCapabilitySchema,
        productLabels: SmartCreationStrategyCapabilitySchema,
        productCatalog: SmartCreationStrategyCapabilitySchema.optional(),
        currentShopSharedHistory: SmartCreationStrategyCapabilitySchema.optional(),
      })
      .strict(),
    targetIntents: z
      .object({
        auto: SmartCreationStrategyCapabilitySchema,
        keyword: SmartCreationStrategyCapabilitySchema,
        product: SmartCreationStrategyCapabilitySchema,
        category: SmartCreationStrategyCapabilitySchema,
        // Optional so persisted pre-brand capability snapshots retain their fingerprint.
        categoryBrand: SmartCreationStrategyCapabilitySchema.optional(),
      })
      .strict(),
  })
  .strict();
export type SmartCreationStrategyCapabilities = z.infer<
  typeof SmartCreationStrategyCapabilitiesSchema
>;

export function createDefaultSmartCreationStrategyCapabilities(): SmartCreationStrategyCapabilities {
  const supported = { supported: true, reason: null } as const;
  return SmartCreationStrategyCapabilitiesSchema.parse({
    lanes: Object.fromEntries(
      SmartCreationStrategyLaneSchema.options.map((lane) => [
        lane,
        lane === "competitor_brand" || lane === "branded_own_brand"
          ? { supported: false, reason: "not_implemented" }
          : supported,
      ]),
    ),
    sources: {
      currentShopSearchTermPerformance: supported,
      currentShopTargetPerformance: supported,
      amazonRecommendations: { supported: false, reason: "not_connected" },
      userInput: supported,
      keywordLibrary: { supported: false, reason: "not_implemented" },
      productLabels: { supported: false, reason: "not_implemented" },
      productCatalog: supported,
      currentShopSharedHistory: supported,
    },
    targetIntents: {
      auto: supported,
      keyword: supported,
      product: supported,
      category: supported,
    },
  });
}

export const SmartCreationStrategyDiagnosticCodeSchema = z.enum([
  "duplicate_target",
  "unsupported_target",
  "cross_shop_target",
  "missing_evidence",
  "stale_evidence",
  "capability_unavailable",
  "mixed_product_scope",
  "ambiguous_classification",
]);
export type SmartCreationStrategyDiagnosticCode = z.infer<
  typeof SmartCreationStrategyDiagnosticCodeSchema
>;

export const SmartCreationStrategyDiagnosticSchema = z
  .object({
    severity: z.enum(["info", "warning", "error"]),
    code: SmartCreationStrategyDiagnosticCodeSchema,
    blocking: z.boolean(),
    strategyKind: SmartCreationTargetingStrategyKindSchema.nullable(),
    targetId: SmartCreationStrategyEntityIdSchema.nullable(),
    message: z.string().trim().min(1).max(1_000),
  })
  .strict()
  .superRefine((diagnostic, ctx) => {
    if (diagnostic.blocking && diagnostic.severity !== "error") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Blocking Strategy diagnostics must use error severity",
        path: ["severity"],
      });
    }
  });
export type SmartCreationStrategyDiagnostic = z.infer<typeof SmartCreationStrategyDiagnosticSchema>;

export const SmartCreationStrategyRecommendationEvidenceSchema = z
  .object({
    status: z.enum(["sufficient", "insufficient", "stale"]),
    primaryWindowDays: z.union([z.literal(14), z.literal(30)]),
    actualWindowDays: z.union([z.literal(14), z.literal(30), z.literal(60)]),
    discoveryWindow: z
      .object({
        windowStart: z.string(),
        windowEnd: z.string(),
        windowDays: z.number().int().positive(),
      })
      .strict()
      .optional(),
    expanded: z.boolean(),
    windowStart: CalendarDateSchema,
    windowEnd: CalendarDateSchema,
    dataThrough: CalendarDateSchema.nullable(),
    canonicalSource: z
      .enum(["current_shop_target_performance", "current_shop_search_term"])
      .nullable(),
    sources: z
      .object({
        searchTerms: z
          .object({
            records: z.number().int().nonnegative(),
            latestDate: CalendarDateSchema.nullable(),
          })
          .strict(),
        targets: z
          .object({
            records: z.number().int().nonnegative(),
            latestDate: CalendarDateSchema.nullable(),
          })
          .strict(),
      })
      .strict(),
    selectedProducts: z.number().int().positive(),
    matchedProducts: z.number().int().nonnegative(),
    limitations: z.array(SmartCreationStrategyDiagnosticCodeSchema).max(20),
  })
  .strict();
export type SmartCreationStrategyRecommendationEvidence = z.infer<
  typeof SmartCreationStrategyRecommendationEvidenceSchema
>;

export const SmartCreationStrategyRecommendedTargetSchema =
  SmartCreationStrategyTargetValueSchema.extend({
    source: SmartCreationStrategyTargetSourceSchema,
    rationale: z.string().trim().min(1).max(1_000),
  }).strict();
export type SmartCreationStrategyRecommendedTarget = z.infer<
  typeof SmartCreationStrategyRecommendedTargetSchema
>;

export const SmartCreationStrategyRecommendedSeedSchema = z
  .object({
    ...SmartCreationStrategySeedRuleValueShape,
    source: SmartCreationStrategyTargetSourceSchema,
    rationale: z.string().trim().min(1).max(1_000),
  })
  .strict()
  .superRefine(validateSmartCreationStrategySeedRule);
export type SmartCreationStrategyRecommendedSeed = z.infer<
  typeof SmartCreationStrategyRecommendedSeedSchema
>;

export const SmartCreationStrategyRecommendationBranchSchema = z
  .object({
    kind: SmartCreationTargetingStrategyKindSchema,
    recommendedEnabled: z.boolean(),
    rationale: z.string().trim().min(1).max(2_000),
    budgetImpact: z
      .object({
        level: z.enum(["none", "low", "medium", "high"]),
        rationale: z.string().trim().min(1).max(1_000),
      })
      .strict(),
    targets: z.array(SmartCreationStrategyRecommendedTargetSchema).max(500),
    seeds: z.array(SmartCreationStrategyRecommendedSeedSchema).max(100),
  })
  .strict();
export type SmartCreationStrategyRecommendationBranch = z.infer<
  typeof SmartCreationStrategyRecommendationBranchSchema
>;

export const SmartCreationStrategyRecommendationSchema = z
  .object({
    id: z.string().uuid(),
    shopId: z.string().uuid(),
    algorithmVersion: z.literal(1),
    basedOnRevision: z.number().int().positive(),
    draftFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
    applicability: SmartCreationRecommendationApplicabilitySchema,
    generatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    evidence: SmartCreationStrategyRecommendationEvidenceSchema,
    capabilities: SmartCreationStrategyCapabilitiesSchema,
    branches: z.array(SmartCreationStrategyRecommendationBranchSchema).length(5),
    diagnostics: z.array(SmartCreationStrategyDiagnosticSchema).max(100),
  })
  .strict()
  .superRefine((recommendation, ctx) => {
    for (const kind of SmartCreationTargetingStrategyKindSchema.options) {
      const occurrences = recommendation.branches.filter((branch) => branch.kind === kind).length;
      if (occurrences !== 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Strategy recommendation requires exactly one ${kind} branch`,
          path: ["branches"],
        });
      }
    }
    const recommendedConfiguration = SmartCreationTargetingStrategyConfigurationSchema.safeParse({
      branches: recommendation.branches.map((branch) => ({
        kind: branch.kind,
        enabled: branch.recommendedEnabled,
        targets: branch.targets.map(({ source, rationale, ...target }) => ({
          ...target,
          provenance: {
            mode: "entered" as const,
            origin: source === "system_required" ? ("system_required" as const) : ("user" as const),
            recordedAt: recommendation.generatedAt,
          },
        })),
        seeds: branch.seeds.map(({ source: _source, rationale: _rationale, ...seed }) => ({
          ...seed,
          provenance: { mode: "entered" as const, recordedAt: recommendation.generatedAt },
        })),
      })),
    });
    if (!recommendedConfiguration.success) {
      for (const issue of recommendedConfiguration.error.issues) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: issue.message,
          path: issue.path,
        });
      }
    }
    const automatic = recommendation.branches.find((branch) => branch.kind === "automatic");
    if (!automatic?.recommendedEnabled) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Automatic Strategy is always recommended as enabled",
        path: ["branches"],
      });
    }
    if (automatic) {
      const expressions = automatic.targets.map((target) =>
        target.intent.type === "auto" ? target.intent.expressionType : null,
      );
      if (
        automatic.targets.length !== SmartCreationAutoTargetExpressionSchema.options.length ||
        expressions.some((expression) => expression === null) ||
        new Set(expressions).size !== SmartCreationAutoTargetExpressionSchema.options.length ||
        !automatic.targets.some((target) => target.included)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Automatic recommendation must retain all four Auto Target candidates and include at least one",
          path: ["branches"],
        });
      }
    }

    const categoryCapability = recommendation.capabilities.targetIntents.category;
    const hasCategoryRecommendation = recommendation.branches.some((branch) =>
      branch.targets.some((target) => target.intent.type === "category"),
    );
    if (!categoryCapability.supported && hasCategoryRecommendation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Category Targets cannot be recommended while the capability is unavailable",
        path: ["branches"],
      });
    }
    if (
      !categoryCapability.supported &&
      !recommendation.diagnostics.some((diagnostic) => diagnostic.code === "capability_unavailable")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unavailable Category capability requires an explicit diagnostic",
        path: ["diagnostics"],
      });
    }

    if (!recommendation.capabilities.sources.amazonRecommendations.supported) {
      const usesAmazonRecommendation = recommendation.branches.some(
        (branch) =>
          branch.targets.some((target) => target.source === "amazon_recommendation") ||
          branch.seeds.some((seed) => seed.source === "amazon_recommendation"),
      );
      if (usesAmazonRecommendation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Amazon recommendations cannot be used while the capability is unavailable",
          path: ["branches"],
        });
      }
    }

    const sourceCapabilities = {
      current_shop_search_term:
        recommendation.capabilities.sources.currentShopSearchTermPerformance,
      current_shop_target_performance:
        recommendation.capabilities.sources.currentShopTargetPerformance,
      amazon_recommendation: recommendation.capabilities.sources.amazonRecommendations,
      user_entered_seed: recommendation.capabilities.sources.userInput,
      product_catalog: recommendation.capabilities.sources.productCatalog ?? {
        supported: true,
        reason: null,
      },
      current_shop_shared_history: recommendation.capabilities.sources.currentShopSharedHistory ?? {
        supported: true,
        reason: null,
      },
    } as const;
    for (const [branchIndex, branch] of recommendation.branches.entries()) {
      for (const [targetIndex, target] of branch.targets.entries()) {
        if (target.source !== "system_required" && !sourceCapabilities[target.source].supported) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Target source ${target.source} is unavailable`,
            path: ["branches", branchIndex, "targets", targetIndex, "source"],
          });
        }
      }
      for (const [seedIndex, seed] of branch.seeds.entries()) {
        if (seed.source !== "system_required" && !sourceCapabilities[seed.source].supported) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Seed source ${seed.source} is unavailable`,
            path: ["branches", branchIndex, "seeds", seedIndex, "source"],
          });
        }
      }
    }
  });
export type SmartCreationStrategyRecommendation = z.infer<
  typeof SmartCreationStrategyRecommendationSchema
>;

export const SmartCreationStrategyBranchSnapshotSchema = z
  .object({
    recordedAt: z.string().datetime(),
    branch: SmartCreationTargetingStrategyBranchSchema,
  })
  .strict();
export type SmartCreationStrategyBranchSnapshot = z.infer<
  typeof SmartCreationStrategyBranchSnapshotSchema
>;

export const SmartCreationStrategyRecommendationSnapshotSchema = z
  .object({
    acceptedAt: z.string().datetime(),
    strategyKind: SmartCreationTargetingStrategyKindSchema,
    recommendation: SmartCreationStrategyRecommendationSchema,
  })
  .strict();
export type SmartCreationStrategyRecommendationSnapshot = z.infer<
  typeof SmartCreationStrategyRecommendationSnapshotSchema
>;

export const SmartCreationStrategyBranchDecisionSchema = z
  .object({
    mode: z.enum(["entered", "accepted", "overridden"]),
    enteredSnapshot: SmartCreationStrategyBranchSnapshotSchema,
    recommendationSnapshot: SmartCreationStrategyRecommendationSnapshotSchema.nullable(),
  })
  .strict()
  .superRefine((decision, ctx) => {
    if (decision.mode !== "entered" && !decision.recommendationSnapshot) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Accepted and overridden Strategy Decisions require recommendation provenance",
        path: ["recommendationSnapshot"],
      });
    }
  });
export type SmartCreationStrategyBranchDecision = z.infer<
  typeof SmartCreationStrategyBranchDecisionSchema
>;

export const SmartCreationStrategyDecisionsSchema = z
  .object({
    automatic: SmartCreationStrategyBranchDecisionSchema,
    research: SmartCreationStrategyBranchDecisionSchema,
    performance: SmartCreationStrategyBranchDecisionSchema,
    branded: SmartCreationStrategyBranchDecisionSchema,
    competitor: SmartCreationStrategyBranchDecisionSchema,
  })
  .strict()
  .superRefine((decisions, ctx) => {
    for (const kind of SmartCreationTargetingStrategyKindSchema.options) {
      const decision = decisions[kind];
      if (decision.enteredSnapshot.branch.kind !== kind) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Entered Strategy snapshot must belong to ${kind}`,
          path: [kind, "enteredSnapshot", "branch", "kind"],
        });
      }
      if (
        decision.recommendationSnapshot &&
        decision.recommendationSnapshot.strategyKind !== kind
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Recommendation Strategy snapshot must belong to ${kind}`,
          path: [kind, "recommendationSnapshot", "strategyKind"],
        });
      }
    }
  });
export type SmartCreationStrategyDecisions = z.infer<typeof SmartCreationStrategyDecisionsSchema>;

export function createDefaultSmartCreationStrategyDecisions(
  strategy: SmartCreationTargetingStrategyConfiguration = createDefaultSmartCreationTargetingStrategyConfiguration(),
  recordedAt = "1970-01-01T00:00:00.000Z",
): SmartCreationStrategyDecisions {
  const configuration = SmartCreationTargetingStrategyConfigurationSchema.parse(strategy);
  const decisionFor = (kind: SmartCreationTargetingStrategyKind) => {
    const branch = configuration.branches.find((candidate) => candidate.kind === kind);
    if (!branch) throw new Error(`Missing ${kind} Strategy branch`);
    return {
      mode: "entered" as const,
      enteredSnapshot: { recordedAt, branch },
      recommendationSnapshot: null,
    };
  };
  return SmartCreationStrategyDecisionsSchema.parse({
    automatic: decisionFor("automatic"),
    research: decisionFor("research"),
    performance: decisionFor("performance"),
    branded: decisionFor("branded"),
    competitor: decisionFor("competitor"),
  });
}
